import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { LocationDto, LocationGetPaginatedDto } from "@/core/api/generated/v1";

import DepartmentInline from "../Department/DepartmentInline";

type LocationAutocompleteOption = BaseAutocompleteOption<LocationDto>;

export const getLocationOptionTitle = (data: LocationDto): string =>
  StringHelper.join([data.name || data.localNumber], ", ", {
    skipEmpty: true,
  });

export const locationToOption = (data: LocationDto): LocationAutocompleteOption => ({
  id: data.id!,
  title: getLocationOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface LocationAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<LocationDto> {
  searchFilters?: Partial<Omit<LocationGetPaginatedDto, "search" | "includeIds">>;
}

export default function LocationAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: LocationAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={locationToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.locationsApi.apiV1LocationsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          locationGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          locationGetPaginatedDto: {
            ...params.locationGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Location'
      placeholder='Search for location...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='location' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>{option.data?.description}</Box>
                      {option?.data?.department?.id && (
                        <DepartmentInline
                          entityId={option.data.department.id}
                          entity={option.data.department}
                          withIcon={false}
                        />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
